/*!
 * Adapted from "The Furrow - ASCIIxGPT - Concept 5" by The Furrow
 * (https://thefurrow.tv/project/asciixgpt). Changes: ported to a WebGL2 shader,
 * with a different pattern set, glyphs, colors, settings, and a carve around content.
 * This file is licensed under CC BY-NC-SA 4.0:
 * https://creativecommons.org/licenses/by-nc-sa/4.0/
 */

import { MAX_LIVE_RIPPLES } from "@bfly/engine";
import { AGE_GLSL } from "../waves/wave-ages.js";
import { WAVE_SHAPE_GLSL } from "../waves/waves.js";
import { ARCH_LIFT, CAP_TAPER } from "./carve.js";
import {
  POP_DECAY,
  POP_HEAD_END,
  POP_RAYS,
  POP_REACH,
  POP_TRAIL_LIFE,
  POP_TRAIL_START,
  TRAIL_DOTS,
  WAKE_GROW,
  WAKE_SAMPLES,
  WAKE_SCALE_SHARE,
  WISP_HULL,
  WISP_SLOTS,
} from "./wisps.js";

// A twinkle swaps a cell's glyph for 2 or 3 of these stars in quick turns. All of them are in
// the field's font (field.ts) and sit centered in the cell.
export const TWINKLE_GLYPHS: readonly string[] = ["✦", "✧", "✸"];
// The size of the shader's palette array.
export const MAX_COLORS = 8;
// The most breakout elements (from field.ts) that the shader accepts.
export const MAX_BREAKOUTS = 4;
// The most waves live at once, as the shaders see it. The ages and palette weights store each
// wave in one RGBA channel, so the `satisfies` fails type checking past 4.
export const MAX_RINGS = MAX_LIVE_RIPPLES satisfies 1 | 2 | 3 | 4;
// The pattern's band frequency, and the size of the field's time steps (field.ts).
export const FRAME_MULTIPLIER = 0.1;
// The pattern's shape: how much each cell column and row shifts its phase.
// `PATTERN_SPEED` runs time at a tenth of The Furrow's speed, so the bands drift slowly.
const PATTERN_X = 0.02;
const PATTERN_Y = 0.27;
const PATTERN_SPEED = 0.1;

/** The charset index (with its fraction) that the pattern gives a cell at a given time,
    like the shader's `patternIndex()`. It's The Furrow's "pulsating" pattern: bands across
    the field that drift over time. The cell is in pattern rows (a viewport cell's row plus
    the scroll shift from parallax.ts), so the bands move like a distant layer. */
export function patternIndex(
  cellX: number,
  cellY: number,
  time: number,
  charsetLen: number,
): number {
  const wave = Math.sin(
    (cellX * PATTERN_X + cellY * PATTERN_Y) * FRAME_MULTIPLIER + time * PATTERN_SPEED,
  );
  return ((wave + 2) / 4) * charsetLen;
}

// The carve's constants and geometry, in GLSL. They read the field shader's `u_origin`,
// `u_cell`, `u_box`, `u_cap`, `u_breakouts`, `u_breakoutCount`, and `u_band`.
// `FRAGMENT_SHADER` includes them, and a test renders them on their own to compare them with
// their twins in carve.ts.
export const CARVE_GLSL = /* glsl */ `
const float ARCH_LIFT = ${ARCH_LIFT.toFixed(2)};
const float CAP_TAPER = ${CAP_TAPER.toFixed(2)};
// How far a cell is outside the carve, in band widths: 0 inside, under 1 in the dithered band
// around it. The carve is the box below the arch's center, plus the arch's circle, so the
// field creeps in around the top of the content. ARCH_LIFT raises the arch by that fraction
// of its height, so the field reaches in less. The breakout boxes add to the carve.
//
// First, the egg end's approximate signed distance (twin of capDistance() in carve.ts).
float capDistance(vec2 point, float halfWidth) {
  float height = u_cap.y - u_cap.x;
  float t = clamp((point.y - u_cap.x) / height, 0.0, 1.0);
  vec2 q = vec2(abs(point.x - 0.5 * (u_box.x + u_box.z)) / (1.0 - CAP_TAPER * t), point.y - u_cap.x);
  vec2 r = vec2(halfWidth, height);
  float k0 = length(q / r);
  float k1 = max(length(q / (r * r)), 1e-6);
  return k0 * (k0 - 1.0) / k1;
}

// How far a cell's center is outside the carve: in device pixels (the return value), and in
// cells (steps), each axis in its own cell size, so the first cell beside the carve is at
// most 1 step out and the next at most 2.
float carveOutside(vec2 cell, out float steps) {
  vec2 center = u_origin + (cell + 0.5) * u_cell;
  float radius = 0.5 * (u_box.z - u_box.x);
  vec2 archCenter = vec2(0.5 * (u_box.x + u_box.z), u_box.y + radius * (1.0 - ARCH_LIFT));
  // The box runs from the arch's center down to the egg end's top.
  bool cap = u_cap.y > u_cap.x;
  vec2 boxEnd = vec2(u_box.z, cap ? u_cap.x : u_box.w);
  vec2 outsideBox = max(max(vec2(u_box.x, archCenter.y) - center, center - boxEnd), 0.0);
  float outside = length(outsideBox);
  steps = length(outsideBox / u_cell);
  // A distance d along the unit direction n is d * length(n / u_cell) cells.
  vec2 fromArch = center - archCenter;
  float arch = max(length(fromArch) - radius, 0.0);
  outside = min(outside, arch);
  steps = min(steps, arch * length(normalize(fromArch) / u_cell));
  if (cap) {
    float end = max(capDistance(center, radius), 0.0);
    vec2 fromCap = center - vec2(archCenter.x, u_cap.x);
    outside = min(outside, end);
    steps = min(steps, end * length(normalize(fromCap) / u_cell));
  }
  for (int i = 0; i < u_breakoutCount; i++) {
    vec4 b = u_breakouts[i];
    vec2 outsideBreakout = max(max(b.xy - center, center - b.zw), 0.0);
    outside = min(outside, length(outsideBreakout));
    steps = min(steps, length(outsideBreakout / u_cell));
  }
  return outside;
}

float carveEdge(vec2 cell) {
  float steps;
  return carveOutside(cell, steps) / u_band;
}
`;

export const VERTEX_SHADER = `#version 300 es
void main() {
  // One triangle that covers the viewport.
  vec2 p = vec2(gl_VertexID & 1, gl_VertexID >> 1) * 4.0 - 1.0;
  gl_Position = vec4(p, 0.0, 1.0);
}`;

// All lengths are in device pixels, measured from the top-left corner.
//
//   u_origin
//   +-----+-----+-----+-----+-----+
//   |  o  |  o  |     |  o  |  o  |   each cell shows one u_atlas sprite, picked by
//   +-----+-----+-----+-----+-----+   patternIndex()
//   |  o  |   .-'''''-.     |     |   the carve is u_box with an arch for its top: a half
//   +-----+ /           \   +-----+   circle as wide as the box. Cells inside it are
//   |  o  | |   u_box   |   |  o  |   clear; cells within u_band outside it are clear with
//   +-----+-+-----------+---+-----+   a probability that falls off with distance (hash()
//                                     against edge)
export const FRAGMENT_SHADER = `#version 300 es
precision highp float;
precision highp int;

uniform sampler2D u_atlas;
uniform int u_charsetLen;
// How many glyph tiers there are, heaviest first, and how many charset entries each one takes
// in a run (GLYPHS and TIER_REPEAT in atlas.ts), so the band can work out a glyph's tier.
uniform ivec2 u_tiers;
uniform float u_time;
// The scroll shift, in rows (parallax.ts): a cell shows the pattern this far below it.
uniform float u_patternShift;
uniform float u_height;
uniform vec2 u_cell;
uniform vec2 u_origin;
uniform vec2 u_grid;
uniform vec4 u_box;
// The egg end: its top and tip, in device pixels from the top. A tip that isn't below the
// top means no egg end.
uniform vec2 u_cap;
// Boxes that break out of the carve's column (the breakouts from field.ts), kept clear like
// the carve, in device pixels: left, top, right, bottom.
const int MAX_BREAKOUTS = ${MAX_BREAKOUTS};
uniform vec4 u_breakouts[MAX_BREAKOUTS];
uniform int u_breakoutCount;
uniform float u_band;
// The palette in OKLab.
uniform vec3 u_colors[${MAX_COLORS}];
uniform int u_colorCount;
// The live waves, oldest first, and each one's palette weight per cell (from the lift
// pass): channel i for ring i.
const int MAX_RINGS = ${MAX_RINGS};
uniform int u_ringCount;
uniform sampler2D u_blend;
// The palette each wave paints, in OKLab: MAX_COLORS slots per wave, of which it uses its
// count. A count of 0 keeps the field's palette (field-palettes.ts).
uniform vec3 u_ringColors[${MAX_RINGS * MAX_COLORS}];
uniform int u_ringColorCount[MAX_RINGS];
// Each atlas sprite is its cell plus this margin on every side, in device pixels.
uniform float u_pad;
// Wall-clock seconds for twinkles. The field's own time steps too slowly for them.
uniform float u_clock;
uniform float u_twinkleRate;
// The wisps (wisps.ts): each one's center (in device pixels), scale, and opacity; the
// pattern cell and time its glyph comes from; and the box outside which its wake is 0.
const int WISP_SLOTS = ${WISP_SLOTS};
uniform vec4 u_wisp[WISP_SLOTS];
uniform vec2 u_wispSource[WISP_SLOTS];
uniform float u_wispTime[WISP_SLOTS];
uniform vec4 u_wakeBox[WISP_SLOTS];
// The wisp trails: per dot, its cell and how inverted it is; per wisp, the bounding box of
// its dots' cells.
const int TRAIL_DOTS = ${TRAIL_DOTS};
uniform vec3 u_trail[WISP_SLOTS * TRAIL_DOTS];
uniform vec4 u_trailBox[WISP_SLOTS];
// The wisp pops (popWisp in wisps.ts): per wisp, the pop's center in device pixels, its age
// in seconds, and 1 (or -1 for no pop); plus the angle of its first ray.
uniform vec4 u_pop[WISP_SLOTS];
uniform float u_popSpin[WISP_SLOTS];
// Each cell's crest (r) and trough (g), from LIFT_SHADER.
uniform sampler2D u_lift;
// The page's color behind the canvas, in sRGB. The canvas can't see the page, and a wisp
// inverts whatever is under it, field and page alike.
uniform vec3 u_ground;

out vec4 color;

// The pattern's shape (patternIndex() in TypeScript).
const float PATTERN_X = ${PATTERN_X.toFixed(2)};
const float PATTERN_Y = ${PATTERN_Y.toFixed(2)};
const float FRAME_MULTIPLIER = ${FRAME_MULTIPLIER.toFixed(2)};
const float PATTERN_SPEED = ${PATTERN_SPEED.toFixed(2)};
// The last part of each index step, which fades into the next color.
const float COLOR_BLEND = 0.5;
// A wave's dust, a wisp's wake, and a wisp's hull change each glyph's scale and opacity. At
// full height, a crest glyph grows by CREST_SCALE, and a trough glyph shrinks by TROUGH_SCALE
// and fades by TROUGH_FADE. The glyph and pattern stay the same; a painting wave blends the
// palette instead (cellColor()).
const float CREST_SCALE = 0.5;
// A twinkle lasts TWINKLE_MIN to TWINKLE_MAX seconds. Each cell runs on its own clock of
// TWINKLE_SLOT seconds, longer than any twinkle, so a cell never overlaps itself.
const int TWINKLE_COUNT = ${TWINKLE_GLYPHS.length};
const float TWINKLE_MIN = 0.8;
const float TWINKLE_MAX = 1.0;
const float TWINKLE_SLOT = 3.0;
// A shine at each star swap: the star grows by SHINE_GROW and turns SHINE_RGB, with a soft
// halo of SHINE_HALO opacity and SHINE_RADIUS cell heights, all fading at SHINE_DECAY per
// second. The halo has to fade out within the 3 x 3 cells that main() paints.
const vec3 SHINE_RGB = vec3(1.0, 0.98, 0.94);
const float SHINE_GROW = 0.6;
const float SHINE_HALO = 0.5;
const float SHINE_RADIUS = 0.55;
const float SHINE_DECAY = 5.0;
const float TROUGH_SCALE = 0.4;
const float TROUGH_FADE = 0.8;
// A wake's crest and trough scale glyphs by this fraction of what a wave's do.
const float WAKE_SCALE_SHARE = ${WAKE_SCALE_SHARE.toFixed(2)};

// Stable per-cell noise in [0, 1], so the carved edge holds still while the field moves.
float hash(uvec2 c) {
  uint h = c.x * 374761393u + c.y * 668265263u;
  h = (h ^ (h >> 13u)) * 1274126177u;
  return float(h ^ (h >> 16u)) / 4294967296.0;
}

${WAVE_SHAPE_GLSL}
// Whether a cell or a neighbor has a crest or a trough (from the lift pass), so a grown
// glyph might spill into this cell.
bool nearWave(vec2 cell) {
  ivec2 last = ivec2(u_grid) - 1;
  for (int dy = -1; dy <= 1; dy++) {
    for (int dx = -1; dx <= 1; dx++) {
      vec4 height = texelFetch(u_lift, clamp(ivec2(cell) + ivec2(dx, dy), ivec2(0), last), 0);
      if (height.r > 0.0 || height.g > 0.0) return true;
    }
  }
  return false;
}

// Whether a point (in device pixels) is in a wisp's wake box, grown by a margin.
bool inWake(int w, vec2 point, float margin) {
  vec4 box = u_wakeBox[w];
  return all(greaterThanEqual(point, box.xy - margin)) && all(lessThanEqual(point, box.zw + margin));
}

// The scale (x) and opacity (y) that the waves, wakes, and wisp hulls give the glyph in a
// cell. LIFT_SHADER works these out once per cell. A cell off the grid stays at rest.
vec2 lift(vec2 cell) {
  if (any(lessThan(cell, vec2(0.0))) || any(greaterThanEqual(cell, u_grid))) return vec2(1.0);
  // r, g: the waves' and hulls' crest and trough. b, a: the wakes'.
  vec4 height = texelFetch(u_lift, ivec2(cell), 0);
  float crest = height.r + WAKE_SCALE_SHARE * height.b;
  float shrink = height.g + WAKE_SCALE_SHARE * height.a;
  return vec2(
    1.0 + CREST_SCALE * crest - TROUGH_SCALE * shrink,
    1.0 - TROUGH_FADE * min(1.0, height.g + height.a));
}

// OKLab to sRGB (Ottosson; twin of oklabToSrgb in the engine's render/color.ts). Fades mix
// in OKLab, like CSS color-mix() does by default, so they step evenly in perceived lightness.
vec3 oklabToSrgb(vec3 lab) {
  vec3 lms = vec3(
    lab.x + 0.3963377774 * lab.y + 0.2158037573 * lab.z,
    lab.x - 0.1055613458 * lab.y - 0.0638541728 * lab.z,
    lab.x - 0.0894841775 * lab.y - 1.2914855480 * lab.z);
  lms = lms * lms * lms;
  vec3 rgb = clamp(vec3(
     4.0767416621 * lms.x - 3.3077115913 * lms.y + 0.2309699292 * lms.z,
    -1.2684380046 * lms.x + 2.6097574011 * lms.y - 0.3413193965 * lms.z,
    -0.0041960863 * lms.x - 0.7034186147 * lms.y + 1.7076147010 * lms.z), 0.0, 1.0);
  return mix(rgb * 12.92, 1.055 * pow(rgb, vec3(1.0 / 2.4)) - 0.055, step(0.0031308, rgb));
}

// The charset index (with its fraction) that the pattern gives a pattern cell at a given
// time, like patternIndex() in TypeScript.
float patternIndex(vec2 cell, float time) {
  float wave = sin((cell.x * PATTERN_X + cell.y * PATTERN_Y) * FRAME_MULTIPLIER + time * PATTERN_SPEED);
  return (wave + 2.0) / 4.0 * float(u_charsetLen);
}

// A viewport cell's index right now. Its pattern cell is the scroll shift below it.
float cellIndex(vec2 cell) {
  return patternIndex(cell + vec2(0.0, u_patternShift), u_time);
}

// Ring i's channel of a cell's weights.
float ringChannel(vec4 weights, int i) {
  return i == 0 ? weights.r : i == 1 ? weights.g : i == 2 ? weights.b : weights.a;
}

// Color k of a palette, wrapping around: the field's (ring -1) or a painting wave's.
vec3 paletteColor(int ring, int k) {
  if (ring < 0) return u_colors[k % u_colorCount];
  return u_ringColors[ring * ${MAX_COLORS} + k % u_ringColorCount[ring]];
}

// The plain color of charset glyph g at an index: colors[g], fading into colors[g + 1] over
// the last COLOR_BLEND of the step, so a cell reaches the next color just as its index
// ticks over.
vec3 glyphLab(float index, int g, int ring) {
  float fade = smoothstep(1.0 - COLOR_BLEND, 1.0, clamp(index - float(g), 0.0, 1.0));
  return mix(paletteColor(ring, g), paletteColor(ring, g + 1), fade);
}
// A cell's glyph color: the field's palette, with each painting wave's palette mixed over the
// older ones by its weight there, in OKLab. It's a blend between each glyph's two colors, not
// a band of size and fade.
vec3 cellColor(float index, int g, vec2 cell) {
  ivec2 last = ivec2(u_grid) - 1;
  vec4 weights = texelFetch(u_blend, clamp(ivec2(cell), ivec2(0), last), 0);
  vec3 lab = glyphLab(index, g, -1);
  for (int i = 0; i < u_ringCount; i++) {
    if (u_ringColorCount[i] > 0) lab = mix(lab, glyphLab(index, g, i), ringChannel(weights, i));
  }
  return oklabToSrgb(lab);
}

// Whether a point in a cell (device pixels from its corner) is on its sprite, margin included.
bool onSprite(vec2 local) {
  return all(greaterThanEqual(local, vec2(-u_pad))) && all(lessThan(local, u_cell + u_pad));
}

// A sprite's alpha at a point in its cell. Linear sampling keeps a grown glyph smooth; at
// scale 1 the samples land on texel centers, so the resting field is pixel-exact. The
// clamp keeps samples off the neighboring sprite. The atlas has the charset in its first
// row and the twinkle stars in its second.
float spriteAlpha(vec2 sprite, vec2 local) {
  vec2 spriteSize = u_cell + 2.0 * u_pad;
  vec2 texel = clamp(local + u_pad, vec2(0.5), spriteSize - 0.5) + sprite * spriteSize;
  return texture(u_atlas, texel / vec2(textureSize(u_atlas, 0))).a;
}

// The star a cell shows right now, or -1. Stateless: each cell runs on its own
// TWINKLE_SLOT-second clock, offset by its hash, and twinkles in a slot with whatever chance
// gives u_twinkleRate twinkles per 1000 cells per second. A twinkle shows 2 or 3 stars in
// equal turns, and no star follows itself. shine is 1 at each swap and fades at SHINE_DECAY.
// glyph() only asks about cells in the edge band.
int twinkle(vec2 cell, out float shine) {
  shine = 0.0;
  if (u_twinkleRate <= 0.0) return -1;
  uvec2 c = uvec2(cell);
  float t = u_clock + hash(c + uvec2(911u, 263u)) * TWINKLE_SLOT;
  float slot = floor(t / TWINKLE_SLOT);
  uvec2 key = c + uint(slot) * uvec2(7919u, 104729u);
  if (hash(key) >= u_twinkleRate / 1000.0 * TWINKLE_SLOT) return -1;
  float duration = mix(TWINKLE_MIN, TWINKLE_MAX, hash(key + uvec2(1u, 0u)));
  float age = t - slot * TWINKLE_SLOT;
  if (age >= duration) return -1;
  int steps = hash(key + uvec2(0u, 1u)) < 0.5 ? 2 : 3;
  float turns = age / duration * float(steps);
  int turn = int(turns);
  // Each swap shines, then fades.
  shine = exp(-fract(turns) * duration / float(steps) * SHINE_DECAY);
  int star = int(hash(key + uvec2(2u, 0u)) * float(TWINKLE_COUNT));
  for (int i = 1; i <= turn; i++) {
    int skip = 1 + int(hash(key + uvec2(3u, uint(i))) * float(TWINKLE_COUNT - 1));
    star = (star + skip) % TWINKLE_COUNT;
  }
  return star;
}

${CARVE_GLSL}

// The trail dot in a cell, if any: how inverted the most inverted one is, and which wisp.
float trailAt(vec2 cell, out int wisp) {
  float level = 0.0;
  wisp = -1;
  for (int w = 0; w < WISP_SLOTS; w++) {
    vec4 box = u_trailBox[w];
    if (any(lessThan(cell, box.xy)) || any(greaterThan(cell, box.zw))) continue;
    for (int k = 0; k < TRAIL_DOTS; k++) {
      vec3 trail = u_trail[w * TRAIL_DOTS + k];
      if (trail.z > level && trail.xy == cell) {
        level = trail.z;
        wisp = w;
      }
    }
  }
  return level;
}

// A pop's dot in a cell, if any: how inverted it is, and which wisp. POP_RAYS rays leave the
// pop's center, and a cell is on a ray where the ray crosses it. A ray's head goes out
// POP_REACH cell heights at most, slowing down (at age t it's the reach times
// 1 - exp(-t / POP_DECAY) out), and is gone by POP_HEAD_END. The cell under the head is fully
// inverted. A cell the head has passed shows a dot that fades from POP_TRAIL_START over
// POP_TRAIL_LIFE, like a trail dot, with a hard ease-out (the cube of the life it has left).
float popAt(vec2 cell, out int wisp) {
  float level = 0.0;
  wisp = -1;
  vec2 center = u_origin + (cell + 0.5) * u_cell;
  float reach = ${POP_REACH.toFixed(1)} * u_cell.y;
  float rayStep = 6.2831853 / ${POP_RAYS.toFixed(1)};
  for (int i = 0; i < WISP_SLOTS; i++) {
    vec4 pop = u_pop[i];
    if (pop.w < 0.0) continue;
    vec2 d = center - pop.xy;
    float away = length(d);
    // The popped wisp's own cell shows no dot.
    if (away < 0.75 * u_cell.y || away > reach) continue;
    float angle = atan(d.y, d.x) - u_popSpin[i];
    float ray = floor(angle / rayStep + 0.5) * rayStep + u_popSpin[i];
    vec2 along = vec2(cos(ray), sin(ray));
    vec2 across = vec2(-along.y, along.x);
    // The cell's half extent across and along the ray.
    float halfAcross = 0.5 * (u_cell.x * abs(across.x) + u_cell.y * abs(across.y));
    float halfAlong = 0.5 * (u_cell.x * abs(along.x) + u_cell.y * abs(along.y));
    if (abs(dot(d, across)) > halfAcross) continue;
    float s = dot(d, along);
    float age = pop.z;
    float head = reach * (1.0 - exp(-age / ${POP_DECAY.toFixed(2)}));
    if (s - halfAlong > head) continue;
    float here = 0.0;
    if (head <= s + halfAlong) {
      here = 1.0 - smoothstep(0.6 * ${POP_HEAD_END.toFixed(2)}, ${POP_HEAD_END.toFixed(2)}, age);
    } else {
      float passed = -${POP_DECAY.toFixed(2)} * log(1.0 - min(s / reach, 0.999));
      float since = age - passed;
      float left = max(0.0, 1.0 - since / ${POP_TRAIL_LIFE.toFixed(2)});
      here = ${POP_TRAIL_START.toFixed(2)} * left * left * left;
    }
    if (here > level) {
      level = here;
      wisp = i;
    }
  }
  return level;
}

// The premultiplied color that a cell's glyph paints at pixel p. Both are relative to
// u_origin, in device pixels. Carved cells paint nothing.
vec4 glyph(vec2 cell, vec2 p) {
  float steps;
  float edge = carveOutside(cell, steps) / u_band;
  if (edge < 1.0 && hash(uvec2(cell)) >= edge) return vec4(0.0);

  // A trail or pop dot shows its wisp's glyph at rest size, in a mix of the wisp's color over
  // the page (inverted) and the cell's own color.
  int trailWisp;
  float trail = trailAt(cell, trailWisp);
  int popWisp;
  float popped = popAt(cell, popWisp);
  if (popped > trail) {
    trail = popped;
    trailWisp = popWisp;
  }
  if (trailWisp >= 0) {
    vec2 at = p - cell * u_cell;
    if (!onSprite(at)) return vec4(0.0);
    float wispIndex = patternIndex(u_wispSource[trailWisp], u_wispTime[trailWisp]);
    int wispGlyph = clamp(int(floor(wispIndex)), 0, u_charsetLen - 1);
    float ownIndex = cellIndex(cell);
    int ownGlyph = clamp(int(floor(ownIndex)), 0, u_charsetLen - 1);
    vec3 own = cellColor(ownIndex, ownGlyph, cell);
    float alpha = spriteAlpha(vec2(float(wispGlyph), 0.0), at);
    return vec4(mix(own, 1.0 - u_ground, trail) * alpha, alpha);
  }

  // Twinkles only appear in the dithered band around the carve. The atlas has the charset
  // in its first row and the twinkle stars in its second.
  float shine;
  int star = edge < 1.0 ? twinkle(cell, shine) : -1;
  // A shining star's halo, around its cell's center.
  vec4 halo = vec4(0.0);
  if (star >= 0) {
    float d = distance(p, (cell + 0.5) * u_cell) / (SHINE_RADIUS * u_cell.y);
    float a = SHINE_HALO * shine * exp(-d * d);
    halo = vec4(SHINE_RGB * a, a);
  }

  // The glyph scales about its cell's center.
  vec2 shape = lift(cell);
  if (star >= 0) shape.x *= 1.0 + SHINE_GROW * shine;
  // The sprite margin lets a grown glyph show the ink that its cell's edge cuts off at rest.
  vec2 local = (p - (cell + 0.5) * u_cell) / shape.x + 0.5 * u_cell;
  if (!onSprite(local)) return halo;

  float index = cellIndex(cell);
  int g = clamp(int(floor(index)), 0, u_charsetLen - 1);
  // Across the band the glyphs shrink toward the carve, like a stippled gradient. A cell's
  // tier runs from the smallest at the carve to its own at the band's outer edge, and the
  // cell rounds it up or down by its own noise, so the share of small glyphs falls off
  // smoothly. The first row is always the smallest. The color stays the cell's own.
  int shapeGlyph = g;
  if (edge < 1.0) {
    int smallest = u_tiers.x - 1;
    int own = (g % (u_tiers.x * u_tiers.y)) / u_tiers.y;
    float tier = mix(float(smallest), float(own), steps <= 1.0 ? 0.0 : edge);
    bool smaller = hash(uvec2(cell) + uvec2(4099u, 7477u)) < fract(tier);
    shapeGlyph = min(int(floor(tier)) + (smaller ? 1 : 0), smallest) * u_tiers.y;
  }
  vec2 sprite = star >= 0 ? vec2(float(star), 1.0) : vec2(float(shapeGlyph), 0.0);
  float alpha = spriteAlpha(sprite, local) * shape.y;
  // A star takes its cell's color, washed toward SHINE_RGB while it shines.
  vec3 rgb = cellColor(index, g, cell);
  if (star >= 0) rgb = mix(rgb, SHINE_RGB, shine);
  vec4 ink = vec4(rgb * alpha, alpha);
  return ink + halo * (1.0 - ink.a);
}

// How much of a pixel the wisps cover: each one's glyph as it was in its source cell when
// it came alive, scaled about its center, at its opacity.
float wispCover(vec2 p) {
  float bare = 1.0;
  for (int w = 0; w < WISP_SLOTS; w++) {
    vec4 wisp = u_wisp[w];
    if (wisp.w <= 0.0) continue;
    vec2 local = (p + u_origin - wisp.xy) / wisp.z + 0.5 * u_cell;
    if (!onSprite(local)) continue;
    float index = patternIndex(u_wispSource[w], u_wispTime[w]);
    int g = clamp(int(floor(index)), 0, u_charsetLen - 1);
    bare *= 1.0 - spriteAlpha(vec2(float(g), 0.0), local) * wisp.w;
  }
  return 1.0 - bare;
}

void main() {
  vec2 p = vec2(gl_FragCoord.x, u_height - gl_FragCoord.y) - u_origin;
  vec2 cell = floor(p / u_cell);
  vec2 center = u_origin + (cell + 0.5) * u_cell;
  // At rest, a pixel only shows its own cell. Near a wave or a wake, a grown glyph spills
  // into the next cells, and so does a shining star near the carve's edge band. There the
  // pixel paints its 3 x 3 neighborhood, with its own cell on top. The margins cover the
  // neighbors' distance.
  float reach = 1.5 * max(u_cell.x, u_cell.y);
  bool near = u_ringCount > 0 && nearWave(cell);
  near = near || (u_twinkleRate > 0.0 && carveEdge(cell) < 1.0 + reach / u_band);
  for (int w = 0; w < WISP_SLOTS; w++) near = near || inWake(w, center, reach);
  vec4 field;
  if (!near) {
    field = glyph(cell, p);
  } else {
    vec4 neighbors = vec4(0.0);
    for (int dy = -1; dy <= 1; dy++) {
      for (int dx = -1; dx <= 1; dx++) {
        if (dx == 0 && dy == 0) continue;
        vec4 c = glyph(cell + vec2(dx, dy), p);
        neighbors = c + neighbors * (1.0 - c.a);
      }
    }
    vec4 own = glyph(cell, p);
    field = own + neighbors * (1.0 - own.a);
  }
  // A wisp inverts what's under it, like mix-blend-mode: difference with a white glyph.
  // under is what the page shows below this pixel. Composited over the page, the output
  // gives under where the wisp covers nothing and 1 - under where it covers everything.
  // Gamma-encoded, like CSS blending.
  vec3 under = field.rgb + u_ground * (1.0 - field.a);
  float cover = wispCover(p);
  vec3 blended = under + cover * (1.0 - 2.0 * under);
  float alpha = 1.0 - (1.0 - field.a) * (1.0 - cover);
  color = vec4(blended - u_ground * (1.0 - alpha), alpha);
}`;

// Each cell's height from the waves' dust, the wisps' wakes, and their hulls, worked out
// once per cell into a texture the size of the grid (in cells). The field reads it (`lift()`
// in its shader). Each wave's age and palette weight per cell go into two more textures.
// Done per pixel instead, the 3 x 3 neighborhood would sum every wake ring nine times.
// The dust and hulls put their crest in r and trough in g, and the wakes put theirs in b
// and a, so the field can scale the wakes less. Rest is exactly 0.
export const LIFT_SHADER = `#version 300 es
precision highp float;
precision highp int;
precision highp sampler2DArray;

const int WISP_SLOTS = ${WISP_SLOTS};
const int WAKE_SAMPLES = ${WAKE_SAMPLES};
const float WAKE_GROW = ${WAKE_GROW.toFixed(3)};
const float WISP_HULL = ${WISP_HULL.toFixed(2)};
// The gust (gust-grains.ts in the engine): at a cell, the fraction of its neighborhood
// (GUST_SMOOTH cells each side, weighted by distance) that a wave has reached, and their mean
// age. The other parts count the wave as arrived where most of the neighborhood is reached
// (wave-ages.ts). The palette's weight rises with that fraction across the edge, from
// BLEND_LOW to BLEND_HIGH, and with the age behind it, over BLEND_TIME seconds of simulation
// time, so no cell's color flips. The dust over a cell raises its crest by GUST_DUST.
const int GUST_SMOOTH = 2;
const float BLEND_LOW = 0.2;
const float BLEND_HIGH = 0.8;
const float BLEND_TIME = 0.24;
const float GUST_DUST = 0.3;

uniform vec2 u_cell;
uniform vec2 u_origin;
// The live waves, oldest first: each one's grain layer (gust-grains.ts), its simulation
// time, and its strength; and where the field's grid sits in the grains' grid, in cells.
const int MAX_RINGS = ${MAX_RINGS};
uniform int u_ringSlot[MAX_RINGS];
uniform float u_ringTime[MAX_RINGS];
uniform float u_ringStrength[MAX_RINGS];
uniform int u_ringCount;
uniform sampler2DArray u_grains;
uniform ivec2 u_grainOffset;
uniform vec4 u_wisp[WISP_SLOTS];
uniform vec4 u_wakeBox[WISP_SLOTS];
// Each wake ring, at texel (sample, wisp): center, radius, and height.
uniform sampler2D u_wakeData;
uniform float u_wakeThick;

layout(location = 0) out vec4 lift;
// Each wave's age at the cell (wave-ages.ts): channel i for ring i, 0 where it hasn't
// reached the cell.
layout(location = 1) out vec4 ages;
// Each wave's palette weight at the cell (cellColor() in the field): channel i for ring i.
layout(location = 2) out vec4 blend;

${WAVE_SHAPE_GLSL}
${AGE_GLSL}

// Wave i at a grain cell: its age byte (0 where it hasn't reached the cell) and its palette
// weight.
vec2 waveAt(ivec2 grain, int i) {
  int slot = u_ringSlot[i];
  float now = u_ringTime[i];
  ivec2 last = textureSize(u_grains, 0).xy - 1;
  float share = 0.0;
  float age = 0.0;
  float weights = 0.0;
  for (int dy = -GUST_SMOOTH; dy <= GUST_SMOOTH; dy++) {
    for (int dx = -GUST_SMOOTH; dx <= GUST_SMOOTH; dx++) {
      float w = exp(-float(dx * dx + dy * dy) / float(GUST_SMOOTH * GUST_SMOOTH));
      float t = texelFetch(u_grains, ivec3(clamp(grain + ivec2(dx, dy), ivec2(0), last), slot), 0).r;
      weights += w;
      if (t <= now) {
        share += w;
        age += w * (now - t);
      }
    }
  }
  if (share <= 0.0) return vec2(0.0);
  float reached = share / weights;
  float meanAge = age / share;
  return vec2(
    reached >= 0.5 ? encodeAge(meanAge) : 0.0,
    smoothstep(BLEND_LOW, BLEND_HIGH, reached) * smoothstep(0.0, BLEND_TIME, meanAge));
}

void main() {
  vec2 cell = floor(gl_FragCoord.xy);
  vec2 center = u_origin + (cell + 0.5) * u_cell;
  ivec2 grain = ivec2(cell) + u_grainOffset;
  // Overlapping waves add up, and so do overlapping wake rings.
  float height = 0.0;
  float encoded[MAX_RINGS];
  float weight[MAX_RINGS];
  for (int i = 0; i < MAX_RINGS; i++) {
    encoded[i] = 0.0;
    weight[i] = 0.0;
  }
  for (int i = 0; i < u_ringCount; i++) {
    vec2 wave = waveAt(grain, i);
    encoded[i] = wave.x;
    weight[i] = wave.y;
    float dust = texelFetch(u_grains, ivec3(grain, u_ringSlot[i]), 0).g;
    height += min(u_ringStrength[i], 1.0) * GUST_DUST * dust;
  }
  float wake = 0.0;
  for (int w = 0; w < WISP_SLOTS; w++) {
    vec4 box = u_wakeBox[w];
    if (any(lessThan(center, box.xy)) || any(greaterThan(center, box.zw))) continue;
    for (int s = 0; s < WAKE_SAMPLES; s++) {
      vec4 ring = texelFetch(u_wakeData, ivec2(s, w), 0);
      if (ring.w <= 0.0) continue;
      float thickness = u_wakeThick + WAKE_GROW * ring.z;
      wake += ring.w * waveShape((distance(center, ring.xy) - ring.z) / thickness, 1.0);
    }
  }
  // A hull is a trough: 1 near a wisp, easing to 0 at WISP_HULL of its size.
  float hull = 0.0;
  for (int w = 0; w < WISP_SLOTS; w++) {
    vec4 wisp = u_wisp[w];
    if (wisp.w <= 0.0) continue;
    float d = distance(center, wisp.xy) / (WISP_HULL * u_cell.y * wisp.z);
    hull = max(hull, wisp.w * (1.0 - smoothstep(0.5, 1.0, d)));
  }
  height = clamp(height - hull, -1.0, 1.0);
  wake = clamp(wake, -1.0, 1.0);
  lift = vec4(max(height, 0.0), max(-height, 0.0), max(wake, 0.0), max(-wake, 0.0));
  ages = vec4(encoded[0], encoded[1], encoded[2], encoded[3]);
  blend = vec4(weight[0], weight[1], weight[2], weight[3]);
}`;
